import React from 'react';
import { BrowserRouter, Routes, Route, Outlet, useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import theme from './theme';
import BusSchedule from './components/BusSchedule';
import ContactForm from './components/ContactForm';
import BottomNav from './components/BottomNav';
import MobilitePanel from './components/MobilitePanel';
import VoiceFeedbackPage from './pages/VoiceFeedbackPage';
import BrandLogos from './components/BrandLogos';

const pageShell = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0a1628 0%, #0f2040 40%, #162d58 100%)',
  pb: 10,
};

function Layout() {
  return (
    <>
      <BrandLogos />
      <Box sx={{ pt: { xs: '88px', sm: '92px', md: '96px' } }}>
        <Outlet />
      </Box>
      <BottomNav />
    </>
  );
}

function SchedulePage() {
  const { stopId, gtfsStopId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const panel = searchParams.get('panel') || 'schedule';
  const id = parseInt(stopId, 10);
  const validId = stopId && !Number.isNaN(id) && id > 0;
  const gtfsId = gtfsStopId || null;
  React.useEffect(() => {
    if (validId) {
      const stopPath = gtfsId ? `/stop/${id}/${gtfsId}` : `/stop/${id}`;
      sessionStorage.setItem('lastStopPath', stopPath);
    }
  }, [id, validId, gtfsId]);
  React.useEffect(() => {
    if (panel === 'mobilite' || panel === 'contact')
      sessionStorage.setItem('lastMobilitePanel', panel);
  }, [panel]);
  if (!validId) return <Navigate to="/404" replace />;
  return (
    <Box sx={pageShell}>
      <Box sx={{ display: panel === 'schedule' ? 'block' : 'none' }}>
        <BusSchedule stopId={id} gtfsStopId={gtfsId} />
      </Box>
      <Box sx={{ display: panel === 'mobilite' ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        {panel === 'mobilite' && (
          <MobilitePanel onContact={() => setSearchParams({ panel: 'contact' })} />
        )}
      </Box>
      <Box sx={{ display: panel === 'contact' ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <ContactPanel onBack={() => setSearchParams({ panel: 'mobilite' })} />
      </Box>
    </Box>
  );
}

function LandingPage() {
  const { t } = useTranslation();

  const steps = [
    { n: '1', key: 'landing.step1' },
    { n: '2', key: 'landing.step2' },
    { n: '3', key: 'landing.step3' },
  ];

  return (
    <Box sx={{ ...pageShell, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stack alignItems="center" spacing={3} sx={{ px: 3, py: 4, textAlign: 'center', maxWidth: 440 }}>
        <Typography sx={{ fontSize: 64, lineHeight: 1, '@keyframes float': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-10px)' } }, animation: 'float 3s ease-in-out infinite' }}>
          🚏
        </Typography>

        <Typography variant="h4" fontWeight={900} sx={{ background: 'linear-gradient(135deg, #fff 0%, #4fc3f7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {t('landing.title')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {t('landing.description')}
        </Typography>

        <Stack spacing={2} sx={{ width: '100%' }}>
          {steps.map((s) => (
            <Paper key={s.n} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderColor: 'rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.04)' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontWeight: 800, fontSize: 16 }}>
                {s.n}
              </Avatar>
              <Typography variant="body2" fontWeight={600} color="rgba(255,255,255,0.85)">
                {t(s.key)}
              </Typography>
            </Paper>
          ))}
        </Stack>

      </Stack>
    </Box>
  );
}

function NotFoundPage() {
  const { t } = useTranslation();

  return (
    <Box sx={{ ...pageShell, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stack alignItems="center" spacing={3} sx={{ px: 3, py: 4, textAlign: 'center', maxWidth: 440 }}>
        <Typography sx={{ fontSize: 64, lineHeight: 1 }}>🔍</Typography>
        <Typography variant="h4" fontWeight={900} color="#fff">
          {t('notFound.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {t('notFound.description')}
        </Typography>
      </Stack>
    </Box>
  );
}

function ContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #0f2040 40%, #162d58 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, pb: 12 }}>
      <Stack spacing={3} sx={{ width: '100%', maxWidth: 560, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/mobilite')} sx={{ color: '#4fc3f7', p: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography fontWeight={700} fontSize={14} color="rgba(255,255,255,0.7)">
            {t('nav.mobility')}
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 3, borderColor: 'rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.04)' }}>
          <ContactForm />
        </Paper>
      </Stack>
    </Box>
  );
}

function ContactPanel({ onBack }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: 560, px: 3, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={onBack} sx={{ color: '#4fc3f7', p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography fontWeight={700} fontSize={14} color="rgba(255,255,255,0.7)">
          {t('nav.mobility')}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3, borderColor: 'rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.04)' }}>
        <ContactForm />
      </Paper>
    </Stack>
  );
}

function MobilitePage() {
  return (
    <Box
      sx={{
        ...pageShell, display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <MobilitePanel />
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/mobilite/voice" element={<VoiceFeedbackPage />} />
            <Route path="/mobilite" element={<MobilitePage />} />
            <Route path="/stop/:stopId" element={<SchedulePage />} />
            <Route path="/stop/:stopId/:gtfsStopId" element={<SchedulePage />} />
            <Route path="/schedule/:stopId" element={<SchedulePage />} />
            <Route path="/stop/:stopId/schedule" element={<SchedulePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
